import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LIMITS } from '@kidzim/shared';
import { Button, Card, Input, Logo } from '../design-system/components';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import type { ApiError } from '../lib/api';

export function ParentAuth() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { loginParent, registerParent } = useAuth();
  const invitedFrom = params.get('invite') ?? '';
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>(
    params.get('mode') === 'register' || invitedFrom ? 'register' : 'login',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inviteCode, setInviteCode] = useState(invitedFrom);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (mode === 'register' && !consent) {
      setError('Please confirm you’re the parent or guardian and consent, so we can set up your kids.');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'register') {
        await registerParent({ email, password, displayName, inviteCode: inviteCode.trim() || undefined, consent });
        nav('/app');
      } else if (mode === 'forgot') {
        await api('/api/auth/password-reset/request', { method: 'POST', body: JSON.stringify({ email }) });
        setForgotSent(true);
      } else {
        await loginParent({ email, password });
        nav('/app');
      }
    } catch (err) {
      const e = err as ApiError;
      setError(messageFor(e, mode));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="kz-center">
      <Card style={{ maxWidth: 420, width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-3)' }}>
          <Logo height={36} />
        </div>
        <p className="kz-muted" style={{ textAlign: 'center', marginBottom: 'var(--space-5)', font: 'var(--type-small)' }}>
          {mode === 'register'
            ? invitedFrom
              ? 'You’ve been invited to join a family. Create your grown-up account below — your code is filled in for you.'
              : 'This is the grown-up’s account. Your kids never need an email or password — they sign in with just a screen name and a secret PIN.'
            : mode === 'forgot'
              ? 'Enter your email and we’ll send a link to reset your password.'
              : 'Sign in to the grown-up’s account. Kids sign in separately with their screen name and PIN.'}
        </p>
        {mode !== 'forgot' && (
          <div className="kz-row" style={{ marginBottom: 'var(--space-5)', gap: 'var(--space-2)' }}>
            <Button variant={mode === 'login' ? 'primary' : 'secondary'} size="sm" onClick={() => setMode('login')}>
              Sign in
            </Button>
            <Button variant={mode === 'register' ? 'primary' : 'secondary'} size="sm" onClick={() => setMode('register')}>
              Create account
            </Button>
          </div>
        )}
        {mode === 'forgot' && forgotSent ? (
          <div className="kz-stack" style={{ gap: 'var(--space-4)' }}>
            <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
              If that email has a KidzIM account, we’ve sent a link to reset the password. Check your inbox.
            </p>
            <Button variant="secondary" onClick={() => { setMode('login'); setForgotSent(false); }}>Back to sign in</Button>
          </div>
        ) : (
          <form className="kz-stack" onSubmit={submit}>
            {mode === 'register' && (
              <Input label="Your name" name="displayName" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
            )}
            <Input label="Email" name="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            {mode !== 'forgot' && (
              <Input
                label="Password"
                name="password"
                type="password"
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === 'register' ? LIMITS.MIN_PARENT_PASSWORD_LEN : undefined}
              />
            )}
            {mode === 'register' && (
              <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
                Use at least {LIMITS.MIN_PARENT_PASSWORD_LEN} characters.
              </p>
            )}
            {mode === 'register' && (
              <Input
                label="Family invite code (optional)"
                name="inviteCode"
                placeholder="e.g. K7QF-9MPX-3RTW"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                autoCapitalize="characters"
              />
            )}
            {mode === 'register' && (
              <label className="kz-row" style={{ alignItems: 'flex-start', gap: 'var(--space-2)', font: 'var(--type-small)' }}>
                <input
                  type="checkbox"
                  name="consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span className="kz-muted">
                  I’m the parent or legal guardian (or have their permission), and I consent to KidzIM collecting
                  my child’s screen name and PIN to run their supervised chats. See our{' '}
                  <Link to="/privacy" target="_blank" rel="noreferrer">Privacy Policy</Link>. We’ll email you to confirm.
                </span>
              </label>
            )}
            {mode === 'login' && (
              <button
                type="button"
                className="kz-muted"
                style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', font: 'var(--type-small)', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => setMode('forgot')}
              >
                Forgot your password?
              </button>
            )}
            {error && <p className="kz-error-text">{error}</p>}
            <Button type="submit" full disabled={busy}>
              {busy ? 'One sec…' : mode === 'register' ? 'Create account' : mode === 'forgot' ? 'Send reset link' : 'Sign in'}
            </Button>
            {mode === 'forgot' && (
              <Button type="button" variant="ghost" full onClick={() => setMode('login')}>Back to sign in</Button>
            )}
          </form>
        )}
      </Card>
    </div>
  );
}

function messageFor(e: ApiError, mode: 'login' | 'register' | 'forgot'): string {
  if (e.error === 'invalid_invite') return 'That family invite code is invalid or has expired. Ask for a fresh one.';
  if (e.error === 'email_unavailable') return 'That email is already in use.';
  if (e.error === 'invalid_credentials') return 'Email or password is incorrect.';
  if (e.error === 'too_many_attempts') return `Too many tries. Please wait ${formatWait(e.retryAfter)} and try again.`;
  if (e.error === 'invalid') return 'Please check the form and try again.';
  if (mode === 'register') return 'Could not create your account.';
  if (mode === 'forgot') return 'Could not send the reset link right now. Try again shortly.';
  return 'Could not sign you in.';
}

function formatWait(seconds?: number): string {
  if (!seconds || seconds < 60) return `${seconds ?? 30} seconds`;
  const mins = Math.ceil(seconds / 60);
  return `${mins} minute${mins === 1 ? '' : 's'}`;
}
